/**
 * /settings/health-check — Account Health Check preferences page.
 *
 * Hidden behind the `digest.enabled` server-side feature flag. When the
 * flag is off, the page renders a "Coming soon" placeholder so admin can
 * still QA the route. Users only see the link in nav once admin flips it.
 *
 * Tier handling:
 *   Ember (free)  → "Send me a sample" button (one-shot, then upsell card)
 *   Steel (pro)   → day/hour/timezone controls + unlimited preview
 *   Damascus (pp) → Steel controls + Monthly Account Review controls
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, MailCheck, ShieldCheck, Clock, Calendar, Send, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import TierPill from '../components/TierPill';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { v: 0, label: 'Monday' },
  { v: 1, label: 'Tuesday' },
  { v: 2, label: 'Wednesday' },
  { v: 3, label: 'Thursday' },
  { v: 4, label: 'Friday' },
  { v: 5, label: 'Saturday' },
  { v: 6, label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  v: i,
  label: `${String(i).padStart(2, '0')}:00`,
}));

const COMMON_TZS = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
  'Asia/Singapore',
  'UTC',
];

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
  } catch (e) {
    return 'Europe/London';
  }
};

const formatNextSend = (iso, tz) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', timeZone: tz,
    });
  } catch (e) {
    return iso;
  }
};

const AccountHealthCheck = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [monthlyPreviewing, setMonthlyPreviewing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    day: 0,
    hour: 7,
    timezone: detectTimezone(),
    monthly_recap_enabled: false,
    monthly_recap_day: 1,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/digest/settings`, { withCredentials: true });
      setSettings(r.data);
      setForm({
        enabled: r.data.enabled,
        day: r.data.day,
        hour: r.data.hour,
        timezone: r.data.timezone || detectTimezone(),
        monthly_recap_enabled: r.data.monthly_recap_enabled,
        monthly_recap_day: r.data.monthly_recap_day,
      });
    } catch (err) {
      console.error('Failed to load digest settings', err);
      toast.error('Could not load your settings. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user) return <Navigate to="/login" replace />;
  if (loading || !settings) {
    return (
      <div className="p-8 text-center text-[#64748B]" data-testid="health-check-loading">
        Loading your preferences…
      </div>
    );
  }

  // Feature flag off → coming soon placeholder.
  if (!settings.feature_enabled) {
    return (
      <div className="max-w-3xl mx-auto p-6 sm:p-8" data-testid="health-check-coming-soon">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center shadow-sm">
          <MailCheck className="w-12 h-12 text-[#0052FF] mx-auto mb-4" />
          <PageTitle
            title="Account Health Check"
            subtitle="A weekly email landing in your inbox with everything coming up — recurring transactions, shortfall warnings and a recap of last week. Coming soon."
            className="mb-0 justify-center"
          />
        </div>
      </div>
    );
  }

  const isEmber = settings.tier === 'free';
  const isSteel = settings.tier === 'pro';
  const isDamascus = settings.tier === 'pro_plus';
  const previewUsed = !!settings.preview_used_at;

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        day: form.day,
        hour: form.hour,
        timezone: form.timezone,
        monthly_recap_enabled: form.monthly_recap_enabled,
        monthly_recap_day: form.monthly_recap_day,
      };
      const r = await axios.put(`${API}/digest/settings`, payload, { withCredentials: true });
      setSettings(r.data);
      toast.success('Saved. Your next health check is locked in.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message) || (typeof detail === 'string' && detail) || 'Could not save. Try again.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const sendPreview = async () => {
    setPreviewing(true);
    try {
      await axios.post(`${API}/digest/preview`, {}, { withCredentials: true });
      toast.success('Preview on its way. Check your inbox in a moment.');
      await load();  // refresh preview_used_at
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message) || 'Could not send the preview.';
      toast.error(msg);
    } finally {
      setPreviewing(false);
    }
  };

  const sendMonthlyPreview = async () => {
    setMonthlyPreviewing(true);
    try {
      await axios.post(`${API}/digest/monthly-preview`, {}, { withCredentials: true });
      toast.success('Monthly Account Review sent. Check your inbox.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message) || 'Could not send the recap.';
      toast.error(msg);
    } finally {
      setMonthlyPreviewing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-8" data-testid="health-check-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <div className="flex items-start gap-3 mb-6">
        <PageTitle
          title={settings.display_name}
          subtitle="A weekly briefing of what's coming up — recurring transactions, shortfall warnings and a recap of last week."
          right={<TierPill user={user} />}
          className="mb-0 flex-1"
        />
      </div>

      {/* Tier capability summary */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-[#0052FF]" />
          <span className="text-xs uppercase tracking-wider font-bold text-[#475569]">
            What you get on {settings.tier === 'free' ? 'Ember' : settings.tier === 'pro' ? 'Steel' : 'Damascus'}
          </span>
        </div>
        <ul className="text-sm text-[#475569] space-y-1.5" data-testid="health-check-capabilities">
          <li>📧 {settings.supports_recurring ? 'Weekly delivery on the day & time you pick' : 'A single sample email — upgrade to Steel for weekly delivery'}</li>
          <li>🔴 Shortfall warnings up to {isDamascus ? '12 weeks' : isSteel ? '4 weeks' : '4 weeks'} ahead</li>
          <li>🏷️ {isDamascus ? 'Spending insights (vs your 8-week baseline)' : 'Spending insights — Damascus exclusive'}</li>
          <li>📅 {settings.supports_monthly_recap ? 'Monthly Account Review on day 1 of each month' : 'Monthly Account Review — Damascus exclusive'}</li>
        </ul>
      </div>

      {/* EMBER: sampler card */}
      {isEmber && (
        <div className="bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7] border border-[#FCD34D] rounded-xl p-5 mb-6" data-testid="health-check-ember-sampler">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-[#020617] mb-1">Try a sample</h3>
              <p className="text-sm text-[#78350F] mb-4">
                See how your week looks on paper. You get <strong>one free sample</strong>; weekly delivery is a Steel perk.
              </p>
              {previewUsed ? (
                <div className="bg-white/60 border border-[#FCD34D] rounded-lg p-3 mb-3">
                  <p className="text-xs text-[#78350F] mb-2">
                    <Lock className="w-3.5 h-3.5 inline-block mr-1" />
                    You&apos;ve already used your free sample.
                  </p>
                  <Link to="/pricing">
                    <Button className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="health-check-ember-upgrade">
                      See Steel plans
                    </Button>
                  </Link>
                </div>
              ) : (
                <Button
                  onClick={sendPreview}
                  disabled={previewing}
                  className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
                  data-testid="health-check-ember-send-sample"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {previewing ? 'Sending…' : 'Send my sample'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STEEL/DAMASCUS: schedule controls */}
      {settings.supports_recurring && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="health-check-schedule-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#0052FF]" />
              <h2 className="font-bold text-[#020617]">Weekly delivery</h2>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="enabled-switch" className="text-sm">{form.enabled ? 'On' : 'Off'}</Label>
              <Switch
                id="enabled-switch"
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                data-testid="health-check-enable-switch"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">Day</Label>
              <Select value={String(form.day)} onValueChange={(v) => setForm((f) => ({ ...f, day: Number(v) }))}>
                <SelectTrigger className="mt-1.5" data-testid="health-check-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">Time</Label>
              <Select value={String(form.hour)} onValueChange={(v) => setForm((f) => ({ ...f, hour: Number(v) }))}>
                <SelectTrigger className="mt-1.5" data-testid="health-check-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h.v} value={String(h.v)}>{h.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">Timezone</Label>
              <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="health-check-tz-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TZS.includes(form.timezone) ? null : (
                    <SelectItem value={form.timezone}>{form.timezone}</SelectItem>
                  )}
                  {COMMON_TZS.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.enabled && settings.next_send_at && (
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#475569] bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-3 py-2" data-testid="health-check-next-send">
              <Clock className="w-4 h-4 text-[#0052FF]" />
              <span>Next health check: <strong>{formatNextSend(settings.next_send_at, form.timezone)}</strong></span>
            </div>
          )}

          <div className="flex gap-2 mt-5">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
              data-testid="health-check-save"
            >
              {saving ? 'Saving…' : 'Save preferences'}
            </Button>
            <Button
              onClick={sendPreview}
              disabled={previewing}
              variant="outline"
              data-testid="health-check-send-preview"
            >
              <Send className="w-4 h-4 mr-2" />
              {previewing ? 'Sending…' : 'Send me a preview now'}
            </Button>
          </div>
        </div>
      )}

      {/* DAMASCUS: monthly recap card */}
      {settings.supports_monthly_recap && (
        <div className="bg-gradient-to-br from-[#EEF2FF] to-[#F0F9FF] border border-[#C7D2FE] rounded-xl p-5 mb-6" data-testid="health-check-monthly-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-[#4338CA]" />
              <h2 className="font-bold text-[#020617]">Monthly Account Review</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#4338CA] text-white">Damascus</span>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="monthly-switch" className="text-sm">{form.monthly_recap_enabled ? 'On' : 'Off'}</Label>
              <Switch
                id="monthly-switch"
                checked={form.monthly_recap_enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, monthly_recap_enabled: v }))}
                data-testid="health-check-monthly-enable-switch"
              />
            </div>
          </div>
          <p className="text-sm text-[#475569] mb-4">
            A bigger, reflective email at the start of every month — top categories, biggest one-offs, savings rate, and YTD totals.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">Day of month</Label>
              <Select value={String(form.monthly_recap_day)} onValueChange={(v) => setForm((f) => ({ ...f, monthly_recap_day: Number(v) }))}>
                <SelectTrigger className="mt-1.5" data-testid="health-check-monthly-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={sendMonthlyPreview}
            disabled={monthlyPreviewing}
            variant="outline"
            data-testid="health-check-monthly-preview"
          >
            <Send className="w-4 h-4 mr-2" />
            {monthlyPreviewing ? 'Sending…' : 'Send a Monthly Review preview'}
          </Button>
        </div>
      )}

      {/* Upsell card for Steel users who want monthly recap */}
      {isSteel && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="health-check-monthly-upsell">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-[#4338CA] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-[#020617] mb-1">Add the Monthly Account Review</h3>
              <p className="text-sm text-[#64748B] mb-3">
                The deeper monthly digest with savings rate, top categories and YTD figures is a Damascus exclusive.
              </p>
              <Link to="/pricing">
                <Button variant="outline" data-testid="health-check-monthly-upgrade">See Damascus plans</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountHealthCheck;
